import React, { useState, useEffect, useMemo } from 'react';
import Swal from 'sweetalert2';
import { rolesService, Rol } from '../src/services/rolesService';

interface RolesProps {
  onClose: () => void;
}

interface RolFormData {
  nombreRol: string;
  descripcion: string;
  activo: boolean;
}

type SortableRolKey = 'nombreRol' | 'activo';

const Roles: React.FC<RolesProps> = ({ onClose }) => {
  // Inject CSS styles for modal animations
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes disminuir {
        0% { transform: scale(1); opacity: 1; }
        100% { transform: scale(0.001); opacity: 0; }
      }
      @keyframes crecer {
        0% { transform: scale(0.001); opacity: 0; }
        100% { transform: scale(1); opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  const [roles, setRoles] = useState<Rol[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<{ key: SortableRolKey; direction: 'asc' | 'desc' } | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalClosing, setIsModalClosing] = useState(false);
  const [editingRole, setEditingRole] = useState<Rol | null>(null);
  const [formData, setFormData] = useState<RolFormData>({ nombreRol: '', descripcion: '', activo: true });
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const loadRoles = async () => {
    try {
      setIsLoading(true);
      setErrorMessage('');
      const data = await rolesService.getAll();
      setRoles(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudieron obtener los roles.';
      setErrorMessage(message);
      showErrorAlert(message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRoles();
  }, []);

  const filteredRoles = useMemo(() => {
    let filtered = roles.filter((rol) =>
      rol.nombreRol.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (rol.descripcion ?? '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (sortConfig) {
      filtered = [...filtered].sort((a, b) => {
        const direction = sortConfig.direction === 'asc' ? 1 : -1;
        if (sortConfig.key === 'activo') {
          return (a.activo === b.activo ? 0 : a.activo ? -1 : 1) * direction;
        }
        const aValue = a[sortConfig.key].toString().toLowerCase();
        const bValue = b[sortConfig.key].toString().toLowerCase();
        if (aValue < bValue) return -1 * direction;
        if (aValue > bValue) return 1 * direction;
        return 0;
      });
    }

    return filtered;
  }, [roles, searchTerm, sortConfig]);

  const showErrorAlert = (message: string) => {
    Swal.fire({
      title: 'Error',
      text: message,
      icon: 'error',
      background: 'rgba(15,23,42,0.85)',
      color: '#fff',
      confirmButtonColor: '#ef4444',
      backdrop: false,
      customClass: {
        popup: 'rounded-2xl'
      }
    });
  };

  const handleSort = (key: SortableRolKey) => {
    setSortConfig((prev) => {
      if (prev && prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const openCreateModal = () => {
    setEditingRole(null);
    setFormData({ nombreRol: '', descripcion: '', activo: true });
    setIsModalOpen(true);
  };

  const openEditModal = (rol: Rol) => {
    setEditingRole(rol);
    setFormData({
      nombreRol: rol.nombreRol,
      descripcion: rol.descripcion ?? '',
      activo: rol.activo
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalClosing(true);
    setTimeout(() => {
      setIsModalOpen(false);
      setIsModalClosing(false);
      setEditingRole(null);
      setFormData({ nombreRol: '', descripcion: '', activo: true });
    }, 300);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSaving(true);

    try {
      if (editingRole) {
        const updated = await rolesService.update(editingRole.rolId, {
          nombreRol: formData.nombreRol,
          descripcion: formData.descripcion || null,
          activo: formData.activo
        });
        setRoles((prev) => prev.map((rol) => (rol.rolId === updated.rolId ? updated : rol)));
        await Swal.fire({
          title: 'Rol actualizado',
          text: 'Los cambios se guardaron correctamente.',
          icon: 'success',
          timer: 2000,
          timerProgressBar: true,
          background: 'rgba(15,23,42,0.85)',
          color: '#fff',
          showConfirmButton: false,
          backdrop: false,
          customClass: {
            popup: 'rounded-2xl'
          }
        });
      } else {
        const created = await rolesService.create({
          nombreRol: formData.nombreRol,
          descripcion: formData.descripcion || null,
          activo: formData.activo
        });
        setRoles((prev) => [created, ...prev]);
        await Swal.fire({
          title: 'Rol creado',
          text: 'El rol fue registrado correctamente.',
          icon: 'success',
          timer: 2000,
          timerProgressBar: true,
          background: 'rgba(15,23,42,0.85)',
          color: '#fff',
          showConfirmButton: false,
          backdrop: false,
          customClass: {
            popup: 'rounded-2xl'
          }
        });
      }
      closeModal();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo guardar el rol.';
      showErrorAlert(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleStatus = (rol: Rol) => {
    Swal.fire({
      title: `¿Deseas ${rol.activo ? 'desactivar' : 'activar'} este rol?`,
      text: rol.nombreRol,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: rol.activo ? '#ef4444' : '#22c55e',
      cancelButtonColor: '#64748b',
      confirmButtonText: rol.activo ? 'Sí, desactivar' : 'Sí, activar',
      cancelButtonText: 'Cancelar',
      background: 'rgba(15,23,42,0.9)',
      color: '#fff',
      backdrop: false,
      customClass: {
        popup: 'rounded-2xl'
      }
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      try {
        const updated = await rolesService.update(rol.rolId, {
          nombreRol: rol.nombreRol,
          descripcion: rol.descripcion ?? '',
          activo: !rol.activo
        });
        setRoles((prev) => prev.map((item) => (item.rolId === updated.rolId ? updated : item)));
      } catch (err) {
        const message = err instanceof Error ? err.message : 'No se pudo actualizar el estado.';
        showErrorAlert(message);
      }
    });
  };

  const handleDelete = (rol: Rol) => {
    Swal.fire({
      title: '¿Eliminar rol?',
      text: rol.nombreRol,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      background: 'rgba(15,23,42,0.9)',
      color: '#fff',
      backdrop: false,
      customClass: {
        popup: 'rounded-2xl'
      }
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      try {
        await rolesService.delete(rol.rolId);
        setRoles((prev) => prev.filter((item) => item.rolId !== rol.rolId));
        await Swal.fire({
          title: 'Rol eliminado',
          text: 'El rol fue eliminado correctamente.',
          icon: 'success',
          background: 'rgba(15,23,42,0.85)',
          color: '#fff',
          confirmButtonColor: '#3b82f6',
          backdrop: false,
          timer: 2000,
          customClass: {
            popup: 'rounded-2xl'
          }
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'No se pudo eliminar el rol.';
        showErrorAlert(message);
      }
    });
  };

  return (
    <div className="pt-4 fixed inset-0 z-50 flex items-start justify-center bg-white dark:bg-background-dark overflow-auto" style={{ animation: 'crecer 0.3s ease-out' }}>
      <div className="w-full max-w-5xl px-2 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="flex items-center gap-2 text-slate-600 dark:text-slate-300 hover:text-primary transition-colors">
              <span className="material-symbols-outlined">arrow_back</span>
              Volver
            </button>
          </div>
          <div className="flex items-center gap-3">
            <span className="material-symbols-outlined text-primary text-2xl">shield</span>
            <h1 className="text-2xl  text-secondary dark:text-white">Roles</h1>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-white/10 transition-colors">
            <span className="material-symbols-outlined text-gray-500 dark:text-gray-400">close</span>
          </button>
        </div>

        {/* Toolbar */}
        <div className="bg-[#307CAF] text-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-4 mb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="flex-1">
              <div className="relative">
                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">search</span>
                <input
                  type="text"
                  placeholder="Buscar por nombre o descripción..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-transparent rounded-full bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span>{filteredRoles.length} roles</span>
              <button
                onClick={loadRoles}
                className="inline-flex items-center gap-1 px-3 py-1 border border-white/20 rounded-full hover:bg-white/10 transition"
                title="Recargar"
              >
                <span className="material-symbols-outlined text-base">refresh</span>
                Recargar
              </button>
              <button
                onClick={openCreateModal}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-[#307CAF] shadow hover:bg-slate-50 transition-colors"
              >
                <span className="material-symbols-outlined text-base">add</span>
                Nuevo Rol
              </button>
            </div>
          </div>
          {errorMessage && (
            <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/50 dark:text-red-200">
              {errorMessage}
            </div>
          )}
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
          {isLoading ? (
            <div className="p-12 text-center text-slate-500 dark:text-slate-400">Cargando roles...</div>
          ) : filteredRoles.length === 0 ? (
            <div className="p-12 text-center text-slate-500 dark:text-slate-400">
              No hay roles que coincidan con tu búsqueda.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead className="bg-[#307CAF] text-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th
                      onClick={() => handleSort('nombreRol')}
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-slate-100/20"
                    >
                      <div className="flex items-center gap-1">
                        Rol
                        {sortConfig?.key === 'nombreRol' && (
                          <span className="material-symbols-outlined text-sm">
                            {sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                      Descripción
                    </th>
                    <th
                      onClick={() => handleSort('activo')}
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-slate-100/20"
                    >
                      <div className="flex items-center gap-1">
                        Estado
                        {sortConfig?.key === 'activo' && (
                          <span className="material-symbols-outlined text-sm">
                            {sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {filteredRoles.map((rol) => (
                    <tr key={rol.rolId} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                      <td className="px-6 py-4">
                        <p className="font-semibold text-slate-900 dark:text-white">{rol.nombreRol}</p>
                        <p className="text-xs text-slate-400">ID #{rol.rolId}</p>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300">
                        {rol.descripcion || 'Sin descripción'}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${
                            rol.activo
                              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                              : 'bg-slate-200 text-slate-700 dark:bg-slate-800/50 dark:text-slate-300'
                          }`}
                        >
                          <span className="material-symbols-outlined text-sm">
                            {rol.activo ? 'verified_user' : 'visibility_off'}
                          </span>
                          {rol.activo ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => openEditModal(rol)}
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-1 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50"
                          >
                            <span className="material-symbols-outlined text-sm">edit</span>
                            Editar
                          </button>
                          <button
                            onClick={() => handleToggleStatus(rol)}
                            className="inline-flex items-center gap-1 rounded-lg border border-transparent px-3 py-1 text-sm text-primary hover:bg-primary/10"
                          >
                            <span className="material-symbols-outlined text-sm">
                              {rol.activo ? 'toggle_off' : 'toggle_on'}
                            </span>
                            {rol.activo ? 'Desactivar' : 'Activar'}
                          </button>
                          <button
                            onClick={() => handleDelete(rol)}
                            className="inline-flex items-center gap-1 rounded-lg border border-transparent px-3 py-1 text-sm text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
                          >
                            <span className="material-symbols-outlined text-sm">delete</span>
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div
            className={`relative w-full max-w-md transform rounded-2xl bg-white dark:bg-slate-800 p-6 shadow-xl transition-all ${isModalClosing ? 'animate-[disminuir_0.3s_ease-in_forwards]' : 'animate-[crecer_0.3s_ease-out_forwards]'}`}
          >
            <div className="rounded-2xl backdrop-blur-sm bg-white/50 dark:bg-slate-800 p-6 shadow-xl dark:border-slate-700 max-h-[90vh] overflow-y-auto">
              <button
                onClick={closeModal}
                className="absolute right-3 top-3 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-1">
                {editingRole ? 'Editar Rol' : 'Crear Rol'}
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
                Define los detalles del rol dentro de la organización.
              </p>
              <form className="space-y-4" onSubmit={handleSubmit}>
                <div>
                  <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">
                    Nombre del Rol
                  </label>
                  <input
                    type="text"
                    required
                    maxLength={50}
                    value={formData.nombreRol}
                    onChange={(e) => setFormData((prev) => ({ ...prev, nombreRol: e.target.value }))}
                    className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Ej. Administrador"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">
                    Descripción
                  </label>
                  <textarea
                    maxLength={150}
                    value={formData.descripcion}
                    onChange={(e) => setFormData((prev) => ({ ...prev, descripcion: e.target.value }))}
                    rows={3}
                    className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Funciones principales del rol..."
                  />
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{formData.descripcion?.length ?? 0}/150</p>
                </div>
                <div className="flex items-center justify-between rounded-xl border border-slate-200 dark:border-slate-700 px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-[#307CAF] dark:text-slate-200">Estado</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Controla si el rol se puede asignar a nuevos usuarios.
                    </p>
                  </div>
                  <label className="inline-flex items-center cursor-pointer">
                    <span className="mr-3 text-sm text-slate-500 dark:text-slate-400">
                      {formData.activo ? 'Activo' : 'Inactivo'}
                    </span>
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={formData.activo}
                      onChange={(e) => setFormData((prev) => ({ ...prev, activo: e.target.checked }))}
                    />
                    <span className={`relative inline-block h-6 w-11 rounded-full transition ${formData.activo ? 'bg-[#307CAF]' : 'bg-slate-400'}`}>
                      <span
                        className={`absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition ${formData.activo ? 'translate-x-5' : ''}`}
                      />
                    </span>
                  </label>
                </div>
                <div className="flex items-center justify-end gap-3 pt-2">
                  <button
                    type="button"
                    className="rounded-lg border border-slate-200 dark:border-slate-700 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50"
                    onClick={closeModal}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="inline-flex items-center gap-2 rounded-lg bg-[#307CAF] px-4 py-2 text-sm font-semibold text-white shadow hover:bg-primary-hover disabled:opacity-60"
                  >
                    {isSaving ? (
                      <>
                        <span className="material-symbols-outlined animate-spin">progress_activity</span>
                        Guardando...
                      </>
                    ) : (
                      <>
                        <span className="material-symbols-outlined">save</span>
                        Guardar
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Roles;
